import React, { Fragment, useContext, useMemo } from "react";
import { AttributeType, StringSubType, AttributeFormatDefinitionDc } from "@evergis/api";
import { uniqueId } from "lodash";
import { AttachmentsAttribute } from "./components/Attachments/AttachmentsAttribute";

import { FeatureCardContext } from "../FeatureCardHeader/FeatureCardContext";
import { FeatureCardCheckbox } from "../FeatureCardCheckbox/FeatureCardCheckbox";
import { AttributeHandBook } from "./components/AttributeHandBook";
import { ImageAttributes } from "../ImageAttributes/ImageAttributes";
import { CalculatedGeometryAttribute } from "./CalculatedGeometryAttribute";
import { AttributeFieldTitle } from "./components/AttibuteFieldTitle";
import { FieldValue, ValueLink, AttributeLabel } from "../../styled";
import { NO_CONTENT_VALUE } from "../../../../utils/constants";
import { isNumberAttribute } from "../../../../interfaces/service";
import { isValidUrl } from "../../../../utils/isValidUrl";
import { isHandbookAttribute } from "../../utils/isHandbookAttribute";
import { formatAttributeValue } from "../../../../utils/formatAttributeValue";
import { getSymbolParameterInfo } from "../../../../components/Classification/getSymbolParameterInfo";
import { DateFormat, formatDate } from "../../../../utils/date";
import { ClassificationParameter } from "../../../../interfaces/classification";
import { FeaturedDcExtendedGeometry } from "../../../../ducks/selectFeatures/types";
import { JSONAttributeValue } from "./types";

export type FeatureAttributeValue =
  | FeaturedDcExtendedGeometry
  | string
  | number
  | boolean
  | Date
  | null
  | JSONAttributeValue[];

export type FeatureAttribute = {
  name: string;
  alias: string;
  description: string;
  value: FeatureAttributeValue;
  type: AttributeType;
  readOnly?: boolean;
  calculatedParameter?: ClassificationParameter | null;
  subType?: StringSubType;
  stringFormat?: AttributeFormatDefinitionDc;
  clientData?: unknown;
};

type FeatureAttributeFieldProps = {
  attribute: FeatureAttribute;
  trim?: boolean;
};

export const NUMBER_DECIMAL_SCALE = 17;

export const isEmptyValue = (value: FeatureAttributeValue): value is null | undefined | "" => {
  return value === void 0 || value === null || value.toString().trim() === "";
};

export const formatValue = (
  value: FeatureAttributeValue,
  type?: AttributeType,
  calculatedParameter?: ClassificationParameter,
) => {
  if (type === AttributeType.DateTime) {
    return formatDate(value as Date, { dateFormat: DateFormat.DateTime });
  }

  const { serialize } = getSymbolParameterInfo(calculatedParameter);

  return (typeof value === "number" ? serialize(value) : value).toString();
};

export const FeatureAttributeField = ({ attribute, trim }: FeatureAttributeFieldProps) => {
  const { layerInfo, setHandBookReference } = useContext(FeatureCardContext);
  const { value, type, alias, name, calculatedParameter, subType, stringFormat, description } = attribute;
  const title = alias || name;
  const valueWithFormatSettings = formatAttributeValue(type, value, stringFormat);
  const isNumberType = useMemo(() => isNumberAttribute(type), [type]);
  const key = uniqueId(name);

  const fieldTitle = useMemo(
    () => <AttributeFieldTitle title={title} description={description} />,
    [title, description],
  );

  if (isHandbookAttribute(name, layerInfo)) {
    return (
      <AttributeHandBook
        key={key}
        layer={layerInfo}
        attribute={attribute}
        setHandBookReference={setHandBookReference}
      />
    );
  }

  if (isEmptyValue(value)) {
    return <FieldValue key={key} field={fieldTitle} value={null} emptyValue={NO_CONTENT_VALUE} />;
  }

  if (subType && subType === StringSubType.Image) {
    return <ImageAttributes key={key} value={value as string} name={name} label={fieldTitle} />;
  }

  if (subType && subType === StringSubType.Attachments) {
    return <AttachmentsAttribute key={key} value={value as JSONAttributeValue[]} label={fieldTitle} />;
  }

  if (valueWithFormatSettings && typeof valueWithFormatSettings === "string") {
    return (
      <FieldValue key={key} field={fieldTitle} value={valueWithFormatSettings} expandable={trim} valueCrop={trim} />
    );
  }

  if (type === AttributeType.Point) {
    return (
      <CalculatedGeometryAttribute
        key={key}
        value={value as FeaturedDcExtendedGeometry}
        label={fieldTitle}
        name={title}
        formattedValue={valueWithFormatSettings as string}
      />
    );
  }

  if (type === AttributeType.Boolean && typeof value === "boolean") {
    return <FeatureCardCheckbox title={fieldTitle} checked={value} />;
  }

  if (type === AttributeType.String && typeof value === "string" && isValidUrl(value)) {
    return (
      <Fragment key={key}>
        <AttributeLabel>{fieldTitle}</AttributeLabel>
        <ValueLink target={"_blank"} href={value}>
          {value}
        </ValueLink>
      </Fragment>
    );
  }

  const valueFormatted = formatValue(value, type, calculatedParameter);

  return (
    <FieldValue
      key={key}
      field={fieldTitle}
      value={valueFormatted}
      title={valueFormatted}
      numeral={isNumberType}
      numeralDecimalScale={NUMBER_DECIMAL_SCALE}
      valueCrop
      expandable
    />
  );
};
